class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    this._observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this._observer.disconnect();
        this._load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
  }

  connectedCallback() {
    this._observer.observe(this);

    // HARD STOP: never allow native form submit inside recommendations
    // (prevents redirect / page reload on submit)
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      e.stopPropagation();

      // If your theme/cart drawer has a global AJAX handler, let it handle the submission.
      // (We only fall back if nothing else will.)
      if (window.Shopify && window.Shopify.onItemAdded) {
        return;
      }

      const toInt = (v, d = 1) => {
        const n = parseInt(String(v ?? ''), 10);
        return Number.isFinite(n) ? n : d;
      };

      const clampQty = (n) => Math.max(1, toInt(n, 1));

      // Variant id: prefer hidden input[name="id"], else active serves button, else any serves button
      const idFromInput = form.querySelector('[name="id"]')?.value;
      const idFromActiveBtn =
        form.querySelector('button.is-active[name="id"][value]')?.value ||
        form.querySelector('.vk-serves-btn.is-active[name="id"][value]')?.value;

      const idFromAnyBtn =
        form.querySelector('button[name="id"][value]')?.value ||
        form.querySelector('.vk-serves-btn[name="id"][value]')?.value;

      const variantId = (idFromInput || idFromActiveBtn || idFromAnyBtn || '').trim();

      // Quantity: prefer input[name="quantity"], else vk-qty dataset/text count
      const qtyInputVal = form.querySelector('[name="quantity"]')?.value;

      const qtyRoot = form.querySelector('.vk-qty');
      const qtyFromDataset = qtyRoot?.dataset?.vkLastQty;
      const qtyFromCountEl =
        qtyRoot?.querySelector('[data-qty-count]')?.textContent ||
        qtyRoot?.querySelector('[data-vk-qty-count]')?.textContent;

      const quantity = clampQty(qtyInputVal || qtyFromDataset || qtyFromCountEl || 1);

      if (!variantId) return;

      // Fallback minimal AJAX add (NO redirect)
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      })
        .then((res) => res.json())
        .then(() => {
          form.classList.add('is-added');
          setTimeout(() => form.classList.remove('is-added'), 1200);
        })
        .catch(() => {});
    });
  }

  _load() {
    const { productId, sectionId, intent } = this.dataset;
    if (!productId || !sectionId) return;

    const url =
      this.dataset.url +
      `&product_id=${productId}&section_id=${sectionId}&intent=${intent || ''}`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Bad response');
        return res.text();
      })
      .then((html) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        const incoming = wrapper.querySelector(
          `product-recommendations[id="${this.id}"]`
        );

        if (!incoming || !incoming.innerHTML.trim()) return;

        this.innerHTML = incoming.innerHTML;
        this.dataset.recommendationsPerformed = 'true';

        // Force image styling via inline styles (nuclear CSS override)
        this._fixCardImages();
        // Variant button injection disabled — recommendation cards are now link-only
        // this._injectVariantButtons();
      })
      .catch(() => {
        this.classList.add('hidden');
      });
  }

  /**
   * Apply inline styles to recommendation card images so they
   * fill their containers properly. Inline styles beat all CSS.
   */
  _fixCardImages() {
    this.querySelectorAll('.vk-card__media img, .vk-card img, article img').forEach(img => {
      img.style.cssText = 'width:100%!important;height:100%!important;object-fit:cover!important;object-position:center!important;display:block!important;padding:0!important;max-height:none!important;background:transparent!important;border-radius:14px!important;aspect-ratio:auto!important;';
    });
    this.querySelectorAll('.vk-card__media').forEach(media => {
      media.style.cssText = 'width:100%!important;aspect-ratio:3/4!important;overflow:hidden!important;border-radius:14px!important;padding:0!important;max-height:none!important;height:auto!important;';
    });
    this.querySelectorAll('article.vk-card').forEach(card => {
      card.style.cssText = 'padding:12px!important;';
    });
  }

  _injectVariantButtons() {
    const cards = this.querySelectorAll('article.vk-card[data-product-id]');
    cards.forEach(card => {
      const link = card.querySelector('a.vk-card__link');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      const match = href.match(/\/products\/([^?#]+)/);
      if (!match) return;

      fetch('/products/' + match[1] + '.js')
        .then(r => r.json())
        .then(product => {
          if (!product.variants || product.variants.length <= 1) return;

          const form = card.querySelector('form');
          if (!form) return;

          // Always remove any existing serves markup and rebuild fresh
          card.querySelectorAll('.vk-card__serves, .vk-serveRow, .vk-servesLabel, .vk-servesRow').forEach(el => el.remove());

          // Build standard serves markup — pushed to bottom of card
          const servesWrap = document.createElement('div');
          servesWrap.className = 'vk-card__serves';
          servesWrap.style.marginTop = 'auto';

          const servesLabel = document.createElement('span');
          servesLabel.className = 'vk-card__serves-label';
          servesLabel.textContent = 'Serves';
          servesWrap.appendChild(servesLabel);

          const servesOptions = document.createElement('div');
          servesOptions.className = 'vk-card__serves-options';

          const firstVariant = product.variants[0];

          product.variants.forEach((v, idx) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'vk-serves-btn' + (idx === 0 ? ' is-active' : '');
            btn.setAttribute('data-serves-id', v.id);
            if (idx === 0) btn.setAttribute('aria-pressed', 'true');
            btn.textContent = v.title;

            btn.addEventListener('click', () => {
              servesOptions.querySelectorAll('.vk-serves-btn').forEach(b => {
                b.classList.remove('is-active');
                b.removeAttribute('aria-pressed');
              });
              btn.classList.add('is-active');
              btn.setAttribute('aria-pressed', 'true');

              const price = card.querySelector('.vk-card__price, [data-price]');
              if (price) {
                price.textContent = (v.price / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
              }
              const input = form.querySelector('input[name="id"], [data-variant-id]');
              if (input) input.value = v.id;
            });

            servesOptions.appendChild(btn);
          });

          servesWrap.appendChild(servesOptions);
          form.parentNode.insertBefore(servesWrap, form);

          const input = form.querySelector('input[name="id"], [data-variant-id]');
          if (input) input.value = firstVariant.id;

          const price = card.querySelector('.vk-card__price, [data-price]');
          if (price) {
            price.textContent = (firstVariant.price / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
          }
        })
        .catch(() => {});
    });
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}